import React, { useEffect, useState } from "react";
import * as Scrivito from "scrivito";
import { ModalHeader } from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import { useTranslate } from "hooks";
import { ProductsOverview } from "components/ProductsOverview/ProductsOverview";
import { useQuery } from "urql";
import { DummyProductSeriesQuery } from "api/queries";
import { useSubType } from "stores/wizardStore";
import {
  useProductsOverviewActions,
  useProductsOverviewStore,
} from "stores/productsOverviewStore";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { useUser } from "stores/user";
import { ModalWrapper } from "../ModalWrapper";
import { useLanguage } from "stores/globalStore";

type Props = {
  dummy: string;
  id: string;
  handleBackAction: () => void;
  type: string;
  groupPosition: number;
};

export const ProductListModal = NiceModal.create(
  Scrivito.connect(
    ({ dummy, id, handleBackAction, type, groupPosition }: Props) => {
      const allProducts = useAllProducts();
      const isLoading = useProductsOverviewStore((state) => state.isLoading);

      const translate = useTranslate();
      const lang = useLanguage();
      const user = useUser();
      const [infoText, setInfoText] = useState("");
      const wizardType = useSubType();

      const isImageFetching = useProductsOverviewStore(
        (state) => state.isImageFetching,
      );

      const [{ data, fetching }] = useQuery({
        query: DummyProductSeriesQuery,
        variables: {
          input: {
            languageCode: lang,
            wizardName: wizardType ?? "",
            dummyAlias: dummy,
            countryCode: user?.tenant ?? "",
          },
        },
        pause: !wizardType,
        requestPolicy: "network-only",
      });

      const { setProductsContent, resetProductConfiguratorStore } =
        useProductsOverviewActions();

      useEffect(() => {
        if (!data) return;
        const getProduct = async () => {
          setInfoText("");
          if (typeof data.dummySeries?.result === "string") {
            setInfoText(data.dummySeries?.result);
          } else {
            setProductsContent(
              (data.dummySeries?.result ?? [])
                .filter((item) => item != null)
                .map((item) => ({
                  ...item,
                  key: item.seriesAlias,
                })),
            );
          }
        };
        if (data.dummySeries?.result?.length !== 0) {
          getProduct();
        }
      }, [data, setProductsContent]);

      const modal = NiceModal.useModal();

      return (
        <ModalWrapper
          isOpen={modal.visible}
          size="xl"
          toggle={modal.remove}
          isLoading={
            !allProducts?.length ||
            data?.dummySeries?.message === undefined ||
            fetching ||
            isLoading ||
            isImageFetching
          }
          onClosed={resetProductConfiguratorStore}
          className={"list"}
          scrollable={true}
        >
          <ModalHeader toggle={modal.remove} tag="div" className="pb-0" />
          {data && data.dummySeries?.message ? (
            <div className="p-5">
              <h3 className="text-uppercase mb-1">
                {translate("PRODUCTS_LIST_MODAL_TITLE")}
              </h3>
              <h4>{translate("WIZARD_PRODUCT_SERIES")}</h4>
              <div>
                {data.dummySeries?.message
                  .trim()
                  .replace(/^"|"$/g, "")
                  .split("\\r\\n\\r\\n")
                  .map((paragraph, key) => (
                    <div key={key} className="mb-5">
                      {paragraph.split("\\r\\n").map((line, linekey) => (
                        <p key={linekey}>{line}</p>
                      ))}
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <ProductsOverview
              {...{
                id,
                infoText,
                isModalActive: true,
                title: translate("PRODUCTS_LIST_MODAL_TITLE"),
                dummy,
                wizardType: wizardType ?? undefined,
                modal,
                handleBackAction,
                type,
                groupPosition,
              }}
            />
          )}
        </ModalWrapper>
      );
    },
  ),
);
